import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * Reasons this tab must not reload onto a newer build right now: a bulk
 * operation mid-flight, an upload, a dirty form, a live interview. While any
 * hold is active the silent upgrade waits — the old build keeps working
 * because its chunks stay on S3 — and clears the moment the last one lifts.
 *
 * Counted per reason so two components holding for the same reason (two
 * uploads) don't release each other.
 */
interface UpgradeHoldState {
  holds: Record<string, number>;
  acquire: (_reason: string) => void;
  release: (_reason: string) => void;
}

export const useUpgradeHoldStore = create<UpgradeHoldState>((set) => ({
  holds: {},
  acquire: (reason) =>
    set((state) => ({
      holds: { ...state.holds, [reason]: (state.holds[reason] ?? 0) + 1 },
    })),
  release: (reason) =>
    set((state) => {
      const count = (state.holds[reason] ?? 0) - 1;
      const holds = { ...state.holds };
      if (count > 0) holds[reason] = count;
      else delete holds[reason];
      return { holds };
    }),
}));

export const selectHoldReasons = (state: UpgradeHoldState): string[] =>
  Object.keys(state.holds);

/** True while anything in the tab has asked the upgrade to wait. */
export const useHasUpgradeHolds = (): boolean =>
  useUpgradeHoldStore((state) => Object.keys(state.holds).length > 0);

/**
 * Hold the silent upgrade while `active`. Releases on unmount, so a component
 * that disappears mid-task cannot pin the tab to the old build forever.
 */
export const useUpgradeHold = (reason: string, active: boolean): void => {
  const acquire = useUpgradeHoldStore((state) => state.acquire);
  const release = useUpgradeHoldStore((state) => state.release);

  useEffect(() => {
    if (!active) return undefined;
    acquire(reason);
    return () => release(reason);
  }, [active, reason, acquire, release]);
};
